import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { handleError } from "src/Common/function";
import { formatDate } from "src/Common/common";
import { setWONumber } from "src/Redux/Slices/HeaderSlice";
import UserService from "src/Services/API/user.service";
import WOService from "src/Services/API/wo.service";
import WOStatusComponent from "src/Pages/WO/Components/Status/wostatuscomponent";
import LoadMoreButton from "src/Components/Common/loadmorebutton";

/**
 * Mockup-styled Work Orders table for the location detail page.
 * Columns: WO Number (link) · Branch · Status · Created Date.
 * Reports the loaded count to the parent so the section header badge can show it.
 */
function CustomerWoMockup({ locationId, customerId, onCountChange }) {
  const user = UserService.getCurrentUser();
  const dispatch = useDispatch();

  const [orders, setOrders] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [loadMoreShown, setLoadMoreShown] = useState(false);
  const latestRequestRef = useRef(0);

  const numericCustomerId = Number(locationId ?? customerId);

  useEffect(() => {
    latestRequestRef.current += 1;
    setOrders([]);
    setPageNumber(0);
    if (!numericCustomerId) {
      setLoadMoreShown(false);
      return;
    }
    fetchOrders([], 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericCustomerId]);

  useEffect(() => {
    if (onCountChange) onCountChange(orders.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders.length]);

  const mergeOrders = (previous, next) => {
    const unique = new Map();
    [...previous, ...next].forEach((order) => unique.set(order.id, order));
    return Array.from(unique.values()).sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
    );
  };

  const fetchOrders = async (previous, pNumber) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await WOService.getAllWorkOrders(user.id, "", pNumber, { customer: numericCustomerId }, null, 0);
      if (requestId !== latestRequestRef.current) return;

      const next = response.data?.orders || [];
      const totalCount = Number(response.data?.totalCount) || 0;
      const merged = mergeOrders(previous, next);
      setOrders(merged);
      setLoadMoreShown(merged.length < totalCount);
    } catch (error) {
      if (requestId === latestRequestRef.current) handleError(error);
    }
  };

  const loadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchOrders(orders, nextPage);
  };

  if (orders.length === 0) {
    return (
      <div className="px-5 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular">
        No work orders found.
      </div>
    );
  }

  return (
    <>
      <table className="w-full table-fixed">
        <thead>
          <tr className="border-b border-borderColor">
            <Th className="w-[120px]">WO Number</Th>
            <Th>Description</Th>
            <Th className="w-[160px]">Branch</Th>
            <Th className="w-[140px]">Status</Th>
            <Th className="w-[140px]">Created Date</Th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id} className="border-b border-borderColor last:border-b-0 hover:bg-backgroundColor">
              <td className="px-5 py-4 align-top">
                <a
                  href={"/work/" + String(order.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => dispatch(setWONumber(String(order.number)))}
                  className="text-[14px] font-OpenSansSemiBold text-blueColor hover:underline"
                >
                  {order.number}
                </a>
              </td>
              <td className="px-5 py-4 align-top text-[14px] font-OpenSansRegular text-subTitleColor break-words whitespace-normal">
                {order.description || "—"}
              </td>
              <td className="px-5 py-4 align-top text-[14px] font-OpenSansRegular text-subTitleColor">
                {order.branch?.name || "—"}
              </td>
              <td className="px-5 py-4 align-top">
                <WOStatusComponent value={order.status} />
              </td>
              <td className="px-5 py-4 align-top text-[14px] font-OpenSansRegular text-subTitleColor">
                {order.createdAt ? formatDate(order.createdAt, "MMM D, YYYY") : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loadMoreShown && (
        <div className="w-full my-3 flex justify-center">
          <LoadMoreButton onClicked={loadMore} />
        </div>
      )}
    </>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={"px-5 py-3 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor " + className}>
      {children}
    </th>
  );
}

export default CustomerWoMockup;
